import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Mail, Lock, User, Phone, Loader2 } from 'lucide-react';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register } = useAuth();
  
  const [activeTab, setActiveTab] = useState("login");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Login form state
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  
  // Register form state
  const [registerData, setRegisterData] = useState({ 
    name: "", 
    email: "", 
    password: "", 
    confirmPassword: "",
    phone: "" 
  });

  const from = location.state?.from || '/client';
  // Only show pending booking message if user came directly from booking page with pendingBooking flag
  const hasPendingBooking = location.state?.pendingBooking === true;
  
  // Check if there's saved booking data in localStorage (for redirect after login)
  const hasSavedBookingData = localStorage.getItem('pendingBooking') !== null;

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const result = await login(loginData.email, loginData.password);
    
    if (result.success) {
      toast.success('Connexion réussie');
      // If user came from booking page with pending booking, redirect back to booking
      let redirectTo;
      if (hasPendingBooking && hasSavedBookingData) {
        redirectTo = '/booking';
      } else if (result.user?.role === 'admin') {
        // Clear any stale pending booking for admin
        localStorage.removeItem('pendingBooking');
        redirectTo = '/admin';
      } else {
        redirectTo = from;
      }
      window.location.href = redirectTo;
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");

    if (registerData.password !== registerData.confirmPassword) {
      setError("Les mots de passe ne correspondent pas");
      return;
    }

    if (registerData.password.length < 6) {
      setError("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }

    setLoading(true);
    const result = await register(
      registerData.name,
      registerData.email,
      registerData.password,
      registerData.phone
    );
    
    if (result.success) {
      toast.success('Compte créé avec succès');
      // If user came from booking page with pending booking, redirect back to booking
      const redirectTo = (hasPendingBooking && hasSavedBookingData) ? '/booking' : from;
      window.location.href = redirectTo;
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen pt-20 bg-[#FAFAFA] flex items-center justify-center py-12 px-6" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/">
            <h1 className="text-3xl font-['Playfair_Display'] text-[#0A0A0A] mb-2">Domaine du Normont</h1>
          </Link>
          <p className="text-[#666666] font-['Outfit']">Accédez à votre espace client</p>
        </div>

        {/* Pending booking notice */}
        {hasPendingBooking && (
          <div className="bg-[#C5A059]/10 border border-[#C5A059] p-4 mb-6 text-center">
            <p className="text-[#0A0A0A] font-['Outfit']">
              <strong>Votre réservation vous attend !</strong>
            </p>
            <p className="text-sm text-[#666666] font-['Outfit'] mt-1">
              Connectez-vous ou créez un compte pour finaliser votre réservation.
            </p>
          </div>
        )}

        <div className="bg-white border border-[#E5E5E5] p-8">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-8">
              <TabsTrigger value="login" data-testid="login-tab">Connexion</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Inscription</TabsTrigger>
            </TabsList>

            {/* Login Form */}
            <TabsContent value="login">
              <form onSubmit={handleLogin} data-testid="login-form">
                <div className="space-y-4">
                  <div>
                    <Label className="font-['Outfit'] mb-2 block">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666666]" />
                      <Input 
                        type="email"
                        value={loginData.email}
                        onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                        className="pl-10"
                        placeholder="votre@email.com"
                        required
                        data-testid="login-email"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="font-['Outfit'] mb-2 block">Mot de passe</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666666]" />
                      <Input 
                        type="password"
                        value={loginData.password}
                        onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                        className="pl-10"
                        placeholder="••••••••"
                        required
                        data-testid="login-password"
                      />
                    </div>
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 p-3 text-sm font-['Outfit']" data-testid="error-message">
                      {error}
                    </div>
                  )}

                  <Button 
                    type="submit" 
                    className="w-full bg-[#C5A059] hover:bg-[#A6864A] text-white py-6"
                    disabled={loading}
                    data-testid="login-submit"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Se connecter'}
                  </Button>
                </div>
              </form>
            </TabsContent>

            {/* Register Form */}
            <TabsContent value="register">
              <form onSubmit={handleRegister} data-testid="register-form">
                <div className="space-y-4">
                  <div>
                    <Label className="font-['Outfit'] mb-2 block">Nom complet</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666666]" />
                      <Input 
                        type="text"
                        value={registerData.name}
                        onChange={(e) => setRegisterData({...registerData, name: e.target.value})}
                        className="pl-10"
                        placeholder="Jean Dupont"
                        required
                        data-testid="register-name"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="font-['Outfit'] mb-2 block">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666666]" />
                      <Input 
                        type="email"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                        className="pl-10"
                        placeholder="votre@email.com"
                        required
                        data-testid="register-email"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="font-['Outfit'] mb-2 block">Téléphone (optionnel)</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666666]" />
                      <Input 
                        type="tel"
                        value={registerData.phone}
                        onChange={(e) => setRegisterData({...registerData, phone: e.target.value})}
                        className="pl-10"
                        placeholder="06 12 34 56 78"
                        data-testid="register-phone"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="font-['Outfit'] mb-2 block">Mot de passe</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666666]" />
                      <Input 
                        type="password"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                        className="pl-10"
                        placeholder="••••••••"
                        required
                        data-testid="register-password"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="font-['Outfit'] mb-2 block">Confirmer le mot de passe</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666666]" />
                      <Input 
                        type="password"
                        value={registerData.confirmPassword}
                        onChange={(e) => setRegisterData({...registerData, confirmPassword: e.target.value})}
                        className="pl-10"
                        placeholder="••••••••"
                        required
                        data-testid="register-confirm-password"
                      />
                    </div>
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 p-3 text-sm font-['Outfit']" data-testid="error-message">
                      {error}
                    </div>
                  )}

                  <Button 
                    type="submit" 
                    className="w-full bg-[#C5A059] hover:bg-[#A6864A] text-white py-6"
                    disabled={loading}
                    data-testid="register-submit"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Créer mon compte'}
                  </Button>
                </div>
              </form>
            </TabsContent>
          </Tabs>
        </div>

        <p className="text-center text-sm text-[#666666] mt-6 font-['Outfit']">
          En créant un compte, vous acceptez nos{' '}
          <Link to="/cgv" className="text-[#C5A059] hover:underline">CGV</Link>
          {' '}et notre{' '}
          <Link to="/legal" className="text-[#C5A059] hover:underline">politique de confidentialité</Link>.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
